import { addError, addSuccess } from '@/store/reducers/ToastStore';
import { CorpusId } from './shared/models/aliases';
import { EventType } from '@/models/Interaction';
import { ExternalTrack } from './shared/analytics/common';
import { getActiveWidgetDataForThread } from '@/store/reducers/PaperFinderWidgetStore';
import { getChatAPI } from '@/api/ChatApi';
import { getCurrentThread } from '../../store/reducers/UIState';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { LoadingSpinnerLogo } from '@/components/system/LoadingSpinnerLogo';
import { Notify } from './shared/models/notifications';
import { optAppConfig } from '@/utils/config';
import { PAPER_FINDER } from 'configs/NoraVariants';
import { PapersPanel } from './shared/components/PapersPanel';
import { PaperTitle } from './overrides/PaperTitle';
import { PermissionsContext } from '../context/PermissionsContext';
import { Reaction } from './shared/models/reactions';
import { Thread } from '../../models/Thread';
import { track } from '@/analytics/track';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { usePaperFinderData } from './shared/hooks/usePaperFinderData';

import { Alert, AlertTitle } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

export function PaperFinder(): JSX.Element {
  const chatApi = getChatAPI();
  const currentThread = useAppSelector(getCurrentThread);
  const widgetData = useAppSelector((state) => {
    return getActiveWidgetDataForThread(state, currentThread?.id);
  });
  const widgetId = widgetData?.id;
  const [baseUrl, setBaseUrl] = useState<string | null>(null);

  useEffect(() => {
    setBaseUrl(null);
    if (currentThread !== null) {
      const threadId = currentThread.id;
      let cancel: boolean = false;
      async function updateProfile(): Promise<void> {
        const { data } = await chatApi.getThreadProfileToolConfig(
          threadId,
          'paper_finder', // gets the config for `paper_finder_standalone` based on profile.
        );
        let baseUrl = data.config.tool_url.replace(/nora.*use-tool/, '');
        // NOTE: this is a special case for running paper finder locally with nora
        // in docker the mabool private and public apis don't match so we make this
        // override
        if (baseUrl.startsWith('http://mabool-router')) {
          baseUrl = 'http://localhost:8081/api/2/';
        }

        if (!cancel) {
          setBaseUrl(baseUrl);
        }
      }
      updateProfile();
      return () => {
        cancel = true;
      };
    }
  }, [currentThread]);

  return (
    <MainLayout>
      {currentThread && widgetId && baseUrl && (
        <PaperFinderForThread
          thread={currentThread}
          widgetId={widgetId}
          baseUrl={baseUrl}
        />
      )}
    </MainLayout>
  );
}

interface PerThreadProps {
  thread: Thread;
  widgetId: string;
  baseUrl: string;
}

function PaperFinderForThread(props: PerThreadProps): JSX.Element {
  const { thread, widgetId, baseUrl } = props;
  const chatApi = getChatAPI();
  const threadId = thread.id;
  const { isReadOnly } = useContext(PermissionsContext);
  const { stateResult, dispatchWidgetAction } = usePaperFinderData(
    baseUrl,
    threadId,
    widgetId,
  );
  const dispatch = useAppDispatch();

  const sendEvent = useCallback(
    async (
      type: EventType,
      corpusId: CorpusId,
      text: string | undefined,
    ): Promise<void> => {
      if (!threadId) {
        getLayoverLogger().logError(
          `threadId not found, needed to send a feedback event from paper finder widget`,
        );
        return;
      }

      try {
        await chatApi.trackEvent({
          type,
          text,
          threadId,
          data: { widget_id: widgetId, corpus_id: corpusId },
        });
      } catch (e: any) {
        // IStore is a secondary storage for feedback/reactions in paper finder so we don't bother notifying the user
        // if this method failed (we will notify the use if the primary method of saving faild)
        getLayoverLogger().logError(
          'Failed sending paper finder paper feedback',
          e,
        );
      }
    },
    [threadId, widgetId],
  );

  const handleReactionChanged = useCallback(
    async (
      corpusId: CorpusId,
      reaction: Reaction | undefined,
    ): Promise<void> => {
      await sendEvent(
        EventType.WIDGET_PAPER_FINDER_PAPER_REACTION,
        corpusId,
        reaction,
      );
    },
    [sendEvent],
  );

  const handleTextFeedbackSubmitted = useCallback(
    async (corpusId: CorpusId, text: string): Promise<void> => {
      await sendEvent(
        EventType.WIDGET_PAPER_FINDER_PAPER_FEEDBACK,
        corpusId,
        text,
      );
    },
    [sendEvent],
  );
  if (stateResult.isLoading) {
    return <LoadingSpinner />;
  }

  if (stateResult.isError) {
    return (
      <Alert variant="filled" severity="error">
        <AlertTitle>Oops, something broke</AlertTitle>
        details:
        {JSON.stringify(stateResult.error)}
      </Alert>
    );
  }

  if (stateResult.isUninitialized || stateResult.data === undefined) {
    return <></>;
  }

  const state = stateResult.data;

  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

  const notify: Notify = (message, type): void => {
    if (type === 'success') {
      dispatch(addSuccess({ text: message }));
    } else {
      dispatch(addError({ text: message }));
    }
  };

  const trackAdapter: ExternalTrack = (eventName, payload): void => {
    track(eventName, { ...payload, threadId, widgetId });
  };

  return (
    <PapersPanel
      baseUrl={baseUrl}
      isReadOnly={isReadOnly}
      widgetState={state}
      dispatchAction={dispatchWidgetAction}
      showCloseWidget={false}
      PaperTitleOverride={
        noraExperienceVariant !== PAPER_FINDER ? PaperTitle : undefined
      }
      notify={notify}
      track={trackAdapter}
      onTextFeedbackSubmitted={handleTextFeedbackSubmitted}
      onReactionChanged={handleReactionChanged}
    />
  );
}

const LoadingSpinner = styled(LoadingSpinnerLogo)`
  margin: 0 !important;
`;

const MainLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
